import { IDestroyOptions, isMobile, Point, Sprite, TextStyle, Ticker } from "pixi.js";
import { GameFont } from "../GameFont";
import { CommonResourcesPackage } from "../resources/CommonResourcesPackage";
import { PlayerModel } from "../models/PlayerModel";
import { AdaptiveText } from "ui-pixijs";
import { Container } from "pixi.js";
import { Spine } from "pixi-spine";
import { SpineResourcesPackage } from "../resources/SpineResourcesPackage";

export class CoinsCounter extends Sprite {
    private static readonly DESKTOP_SCALE_FROM = 1;
    private static readonly DESKTOP_SCALE_TO = 1.2;
    private static readonly MOBILE_SCALE_FROM = 0.85;
    private static readonly MOBILE_SCALE_TO = 1;
    private static get SCALE_FROM(): number {
        return isMobile.phone ? CoinsCounter.MOBILE_SCALE_FROM : CoinsCounter.DESKTOP_SCALE_FROM;
    }
    private static get SCALE_TO(): number {
        return isMobile.phone ? CoinsCounter.MOBILE_SCALE_TO : CoinsCounter.DESKTOP_SCALE_TO;
    }
    private static readonly scaleDurationMS = 150;
    private readonly icon: Sprite;
    private readonly substrate: Sprite;
    private readonly count: AdaptiveText;
    private readonly queueOfCounts: number[] = [];

    private activeTickers = new Set<(_dt: number) => void>();
    private cancelled: boolean = false;

    public get coinFlyTargetCoordinates(): Point {
        const icon = this.icon;
        const gv = this.parent as Container;
        const lb = icon.getLocalBounds();
        const localCenter = new Point(lb.x + lb.width / 2, lb.y + lb.height / 2);

        return gv ? gv.toLocal(localCenter, icon) : localCenter; // Convert from icon's local space to GameView's local space
    }

    public constructor(
        private readonly playerModel: PlayerModel,
        commonResources: CommonResourcesPackage,
        private readonly spineResources: SpineResourcesPackage
    ) {
        super(commonResources.assets_common_violet_placeholder_png);
        this.eventMode = "static";
        this.pivot.set(commonResources.assets_common_violet_placeholder_png.width * 0.5, commonResources.assets_common_violet_placeholder_png.height * 0.5);
        this.icon = new Sprite(commonResources.assets_common_coin_icon_png);
        this.icon.position.set(30, 16);

        this.substrate = new Sprite(commonResources.assets_common_coints_substrate_png);
        this.substrate.position.set(142, 34);

        this.count = new AdaptiveText(playerModel.coins.value.toString(), new CountTextStyle(), 190, 56);
        this.substrate.addChild(this.count);
        this.count.updateText(false);
        this.count.position.set((this.substrate.width - this.count.width) * 0.5, (this.substrate.height - this.count.height) * 0.5);

        this.addChild(
            this.icon,
            this.substrate
        );
        this.playerModel.syncCoinsWithoutAnimation.add(this.syncCoins, this);
    }

    public cancelAnimations(): void {
        this.cancelled = true;

        // Remove all active ticker callbacks
        for (const fn of this.activeTickers) {
            Ticker.shared.remove(fn);
        }
        this.activeTickers.clear();
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.cancelAnimations();
        this.playerModel.syncCoinsWithoutAnimation.remove(this.syncCoins, this);
        super.destroy(options);
    }

    public async addCoins(value: number): Promise<void> {
        return new Promise<void>((resolve) => {
            const startValue = Number.parseInt(this.count.text);
            let endValue: number;
            if (this.queueOfCounts.length === 0) {
                endValue = startValue + value;
                this.queueOfCounts.push(endValue);
            } else {
                endValue = this.queueOfCounts[this.queueOfCounts.length - 1] + value;
                this.queueOfCounts.push(endValue);
            }

            const duration = 500;
            let elapsed = 0;

            const ticker = (_dt: number) => {
                if (this.cancelled) {
                    Ticker.shared.remove(ticker);
                    resolve();
                    return;
                }
                elapsed += Ticker.shared.elapsedMS;
                const t = Math.min(elapsed / duration, 1);
                const progress = 1 - Math.pow(1 - t, 3); // ease-out cubic

                const displayValue = Math.floor(startValue + (endValue - startValue) * progress);
                this.count.text = displayValue.toString();

                if (t >= 1) {
                    Ticker.shared.remove(ticker);
                    this.queueOfCounts.shift();
                    this.count.text = endValue;
                    this.activeTickers.delete(ticker);
                    this.glow();
                    resolve();
                }
            };

            this.activeTickers.add(ticker);
            Ticker.shared.add(ticker);
        });
    }

    public async syncCoins(): Promise<void> {
        this.count.text = this.playerModel.coins.value;
    }

    public async playFinalAnimation(increment: number): Promise<void> {
        // 1. Scale up
        await this.animateScale(CoinsCounter.SCALE_FROM, CoinsCounter.SCALE_TO, CoinsCounter.scaleDurationMS);

        // 2. Update value while "popped"
        await this.addCoins(increment);

        // 3. Scale back to normal
        await this.animateScale(CoinsCounter.SCALE_TO, CoinsCounter.SCALE_FROM, CoinsCounter.scaleDurationMS);
    }

    private async animateScale(from: number, to: number, duration: number): Promise<void> {
        return new Promise(resolve => {
            if (this.cancelled) return resolve();
            let elapsed = 0;

            const step = (_dt: number) => {
                if (this.cancelled) {
                    Ticker.shared.remove(step);
                    resolve();
                    return;
                }
                elapsed += Ticker.shared.elapsedMS;
                const t = Math.min(elapsed / duration, 1);
                const value = from + (to - from) * t;
                this.scale.set(value);

                if (t >= 1) {
                    Ticker.shared.remove(step);
                    this.activeTickers.delete(step);
                    resolve();
                }
            };

            this.activeTickers.add(step);
            Ticker.shared.add(step);
        });
    }

    private glow(): void {
        const iconGlow = new Spine((this.spineResources.assets_spine_coin_coin_glow_json as any).spineData);
        iconGlow.position.set(this.icon.width * 0.5, this.icon.height * 0.5);
        iconGlow.state.setAnimation(0, "coin_glow2", false);
        this.icon.addChild(iconGlow);

        iconGlow.state.addListener({
            complete: () => {
                requestAnimationFrame(() => {
                    if (iconGlow && iconGlow.parent) {
                        this.icon.removeChild(iconGlow);
                    }
                    if (iconGlow) {
                        iconGlow.destroy();
                    }
                });
            }
        });
    }
}

class CountTextStyle extends TextStyle {
    public constructor() {
        super({
            fontSize: 40,
            fill: "#FFFFFF",
            fontFamily: GameFont.Default,
            dropShadow: true,
            dropShadowColor: "#7500d4",
            dropShadowBlur: 0.5,
            dropShadowDistance: 4,
            dropShadowAngle: 1.77
        });
    }
}
